import React, { useState } from 'react';
import SPAButton from '../shared/SPAButton';
import SPAPill from '../shared/SPAPill';
import SPASectionHeading from '../shared/SPASectionHeading';
import useRepairHistory from './useRepairHistory';
import type { RepairEntry } from './useRepairHistory';
import { GradeSyncLink } from './gradeSyncPath';

type Refusal = { response?: { data?: { errors?: string[] } } };

const ACTION_LABELS: Record<RepairEntry['action'], string> = {
  bulk_unassign: 'Unassigned (all)',
  redrive: 'Re-sent grades',
  repoint: 'Moved link',
  unassign: 'Unassigned',
};

const when = (at: string) => new Date(at).toLocaleString();

// Undo asks once more, inline — it changes the teacher's assignments as surely as the repair did.
const UndoControl = ({ busy, entry, onUndo }: { busy: boolean; entry: RepairEntry; onUndo: () => void }) => {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <SPAButton size="xs" type="button" variant="secondary" onClick={() => setConfirming(true)}>Undo</SPAButton>
    );
  }

  return (
    <span className="tw-flex tw-items-center tw-gap-2">
      <span className="tw-text-sm tw-text-spa-secondary">
        {entry.batch_id == null ? 'Undo this?' : 'Undo it for every assignment in this change?'}
      </span>
      <SPAButton disabled={busy} size="xs" type="button" variant="danger" onClick={onUndo}>Undo</SPAButton>
      <SPAButton size="xs" type="button" variant="secondary" onClick={() => setConfirming(false)}>Keep</SPAButton>
    </span>
  );
};

/** What support changed for this teacher, newest first — the trail and the way back. */
const RepairHistory = ({ stafferId }: { stafferId: string }) => {
  const { history, undo } = useRepairHistory(stafferId);
  const entries = history.data ?? [];
  const refusal = (undo.error as Refusal)?.response?.data?.errors?.[0];
  // A bulk unassign is one change across many assignments; show it once, undo it once.
  const shown = entries.filter((entry, index) => (
    entry.batch_id == null || entries.findIndex(other => other.batch_id === entry.batch_id) === index
  ));
  const count = shown.length > 0 ? `${shown.length} ${shown.length === 1 ? 'change' : 'changes'}` : undefined;
  const batchSize = (entry: RepairEntry) => entries.filter(other => other.batch_id === entry.batch_id).length;

  return (
    <div className="tw-mt-8">
      <SPASectionHeading meta={count} title="History" />
      {history.isLoading && (
        <ul aria-hidden="true" className="tw-m-0 tw-list-none tw-p-0">
          {[0, 1].map(index => (
            <li
              key={index}
              className="tw-animate-pulse tw-border-0 tw-border-b tw-border-solid tw-border-spa-border tw-py-2"
            >
              <div className="tw-h-5 tw-w-72 tw-rounded tw-bg-spa-bg-skeleton" />
              <div className="tw-mt-1.5 tw-h-3.5 tw-w-48 tw-rounded tw-bg-spa-bg-skeleton-subtle" />
            </li>
          ))}
        </ul>
      )}
      {!history.isLoading && shown.length === 0 && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">No changes from this dashboard yet.</p>
      )}
      {refusal != null && <p className="tw-m-0 tw-mb-2 tw-text-sm tw-text-spa-error">{refusal}</p>}
      <ul className="tw-m-0 tw-list-none tw-p-0">
        {shown.map(entry => (
          <li
            key={entry.id}
            className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3 tw-border-0 tw-border-b
              tw-border-solid tw-border-spa-border tw-py-2"
          >
            <div className="tw-text-sm">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <SPAPill tone={entry.undone_at == null ? 'neutral' : 'warning'}>{ACTION_LABELS[entry.action]}</SPAPill>
                <span className="tw-text-spa-primary">
                  {entry.batch_id == null ? entry.summary : `${entry.summary} · ${batchSize(entry)} assignments`}
                </span>
              </div>
              <div className="tw-mt-0.5 tw-text-sm tw-text-spa-tertiary">
                {`${when(entry.created_at)} · ${entry.actor ?? 'Unknown'}`}
                {entry.batch_id == null && (
                  <>
                    {' · '}
                    <GradeSyncLink
                      to={`/assignments/${entry.assignment_id}`}
                    >
                      {entry.assignment_title ?? `Assignment ${entry.assignment_id}`}
                    </GradeSyncLink>
                  </>
                )}
                {entry.undone_at != null && ` · undone ${when(entry.undone_at)} by ${entry.undone_by ?? 'Unknown'}`}
              </div>
            </div>
            {entry.undoable && (
              <UndoControl busy={undo.isLoading} entry={entry} onUndo={() => undo.mutate(entry.id)} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RepairHistory;
